/*
 * FOR SALE — liens Discord ouverts dans l'application de bureau (Windows, macOS) avant le navigateur, pour profiter de
 * la session déjà ouverte dans l'application. Module chargé par la mise en page for-sale-demo (accueil et pages des
 * annonces). Textes du message de repli : dictionnaire de la langue de la page (fs-i18n.js, nav.discord.*).
 *
 *  - Amélioration progressive : le href reste l'adresse https (sans JavaScript, copie, clic du milieu, Ctrl/Cmd+clic).
 *    Seul le clic gauche simple (ou Entrée) sur un lien de salon, de fil ou d'invitation est intercepté, par délégation
 *    sur le document : les liens ajoutés plus tard (fiche clonée, ticket) sont couverts. Jamais l'OAuth, l'API ou le CDN.
 *  - Ordinateurs Windows et macOS seulement ; mobiles, tablettes (iPad compris), Linux et cas inconnus gardent le lien
 *    web, qui ouvre déjà l'application sur mobile.
 *  - Tentative : discord://… (cadre caché sous Firefox, qui remplacerait sinon la page par une erreur si l'application
 *    manque), application reconnue par blur, pagehide ou visibilitychange (hidden) dans les WAIT_MS suivantes. Sinon,
 *    adresse https comme le lien l'aurait ouverte (nouvel onglet pour target=_blank, sinon même onglet) ; fenêtre
 *    bloquée : message role="status" près du lien, « Ouvrir dans le navigateur » (vrai lien https). Une application
 *    reconnue après le repli ne relance rien.
 *  - Mémoire du visiteur (localStorage « fs-discord-open », lu et écrit sous try/catch) : application reconnue → on
 *    continue de l'essayer ; repli → liens web directs pendant WEB_DAYS jours. « Essayer l'application Discord » (message
 *    de repli) efface ce choix et relance la tentative.
 *  - window.FSDiscord.open(url, { newTab }) : même parcours pour les navigations lancées par le code.
 * Les fonctions pures et la fabrique sont exportées pour les tests Node.
 */
import { t } from './fs-i18n.js';

/**
 * Attente de l'application : 2,5 s. Sans application, Chrome et Edge ne font rien et le repli part au bout de ce délai,
 * une seule fois (le choix « web » est ensuite gardé). Avec l'application, la boîte « Ouvrir Discord ? » de Chrome au
 * premier usage doit tenir dans ce délai si elle ne retire pas le focus à la page : 2,5 s laissent le temps de la lire et
 * d'accepter, et restent sous les 5 s d'activation utilisateur de Chrome et Firefox qui autorisent l'ouverture d'onglet.
 */
export const WAIT_MS = 2500;
export const WEB_DAYS = 30;
const DAY_MS = 86_400_000;
export const STORAGE_KEY = 'fs-discord-open';
export const ATTEMPT_EVENT = 'fs:discord-app';

const SNOWFLAKE = /^\d{17,20}$/;
const INVITE = /^[A-Za-z0-9-]{2,64}$/;
const APP_HOSTS = new Set(['discord.com', 'ptb.discord.com', 'canary.discord.com']);
const TARGETS = new Set(['', '_self', '_blank', '_top', '_parent']);

/**
 * Adresse de l'application pour un lien https de salon, de fil ou de message (discord.com, ptb., canary.) ou
 * d'invitation (discord.gg/<code>, discord.com/invite/<code>) ; '' pour tout le reste (OAuth, API, CDN, autre hôte,
 * port, identifiants, requête, fragment, chemin inattendu).
 */
export function appUrl(raw) {
  let url;
  try { url = new URL(String(raw ?? '').trim()); } catch { return ''; }
  if (url.protocol !== 'https:' || url.username || url.password || url.port || url.search || url.hash) return '';
  const parts = url.pathname.replace(/\/$/, '').split('/').slice(1);
  if (url.hostname === 'discord.gg') return parts.length === 1 && INVITE.test(parts[0]) ? `discord://-/invite/${parts[0]}` : '';
  if (!APP_HOSTS.has(url.hostname)) return '';
  if (parts[0] === 'invite') return parts.length === 2 && INVITE.test(parts[1]) ? `discord://-/invite/${parts[1]}` : '';
  if (parts[0] === 'channels' && parts.length >= 3 && parts.length <= 4 && parts.slice(1).every((part) => SNOWFLAKE.test(part))) {
    return `discord://-/${parts.join('/')}`;
  }
  return '';
}

/**
 * Ordinateur visé : 'windows' | 'macos' | ''. userAgentData.platform, sinon navigator.platform, sinon l'agent. Exclus :
 * mobiles et tablettes (iPad qui se présente comme un Mac : maxTouchPoints > 1), Linux, Chrome OS, cas inconnus.
 */
export function desktopPlatform(nav = {}) {
  const agent = String(nav.userAgent ?? '');
  if (nav.userAgentData?.mobile || /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(agent)) return '';
  const name = String(nav.userAgentData?.platform || nav.platform || '');
  if (/^win/i.test(name) || (!name && /Windows NT/.test(agent))) return 'windows';
  if (/^mac/i.test(name) || (!name && /Macintosh|Mac OS X/.test(agent))) return Number(nav.maxTouchPoints) > 1 ? '' : 'macos';
  return '';
}

/** Clic gauche simple, sans touche modificatrice, pas déjà traité ailleurs. Entrée sur un lien donne aussi button 0. */
export const plainClick = (event) => Boolean(event) && !event.defaultPrevented && event.button === 0
  && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Lien Discord à tenter dans l'application pour ce clic : { link, url, app, newTab }, sinon null. Exclus : liens de
 * téléchargement, cibles nommées et liens web voulus ([data-fs-discord-web], ceux du message de repli).
 */
export function discordClick(event) {
  if (!plainClick(event)) return null;
  const link = event.target?.closest?.('a[href]');
  if (!link || link.hasAttribute('download') || link.hasAttribute('data-fs-discord-web')) return null;
  const target = String(link.getAttribute('target') || '').toLowerCase();
  if (!TARGETS.has(target)) return null;
  const app = appUrl(link.href);
  return app ? { link, url: link.href, app, newTab: target === '_blank' } : null;
}

/** Choix gardé : 'app' | 'web' (moins de WEB_DAYS jours, jamais daté du futur) | ''. */
export function readChoice(raw, now) {
  try {
    const value = JSON.parse(raw);
    if (value?.mode === 'app') return 'app';
    if (value?.mode === 'web' && Number.isFinite(value.at) && value.at <= now + 60_000 && now - value.at < WEB_DAYS * DAY_MS) return 'web';
  } catch { /* valeur absente ou illisible */ }
  return '';
}

/**
 * Parcours d'ouverture pour une fenêtre `win` (window, ou faux objet des tests : document, navigator, location, open,
 * localStorage, setTimeout, clearTimeout, add/removeEventListener). `notify(state, kind)` affiche le message de repli
 * ('blocked' : fenêtre refusée, 'web' : ouvert dans un nouvel onglet, 'clear' : retiré) ; `now` : horloge.
 */
export function createOpener(win, { notify = () => {}, now = () => Date.now() } = {}) {
  const doc = win.document;
  const nav = win.navigator ?? {};
  const desktop = Boolean(desktopPlatform(nav));
  const gecko = /Firefox\//.test(String(nav.userAgent ?? ''));
  let pending = null;

  const storage = {
    get() { try { return win.localStorage.getItem(STORAGE_KEY); } catch { return null; } },
    set(mode) { try { win.localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, at: now() })); } catch { /* sans mémoire */ } },
    clear() { try { win.localStorage.removeItem(STORAGE_KEY); } catch { /* sans mémoire */ } },
  };
  const prefersWeb = () => readChoice(storage.get(), now()) === 'web';

  /** Adresse https comme le lien l'aurait ouverte ; false si la fenêtre a été refusée. */
  function openWeb(url, newTab) {
    if (!newTab) { win.location.assign(url); return true; }
    let opened = null;
    try { opened = win.open(url, '_blank'); } catch { opened = null; }
    if (!opened) return false;
    try { opened.opener = null; } catch { /* fenêtre d'une autre origine */ }
    return true;
  }

  function launch(app) {
    if (!gecko) { win.location.href = app; return; }
    const frame = doc.createElement('iframe');
    frame.hidden = true;
    frame.src = app;
    doc.body.append(frame);
    win.setTimeout(() => frame.remove(), WAIT_MS * 2);
  }

  function attempt(state) {
    const app = appUrl(state.url);
    let timer = 0;
    const settle = (opened) => {
      if (pending !== state) return;
      pending = null;
      win.clearTimeout(timer);
      win.removeEventListener('blur', seen);
      win.removeEventListener('pagehide', seen);
      doc.removeEventListener('visibilitychange', hidden);
      storage.set(opened ? 'app' : 'web');
      if (opened) { notify(state, 'clear'); return; }
      notify(state, openWeb(state.url, state.newTab) ? 'web' : 'blocked');
    };
    const seen = () => settle(true);
    const hidden = () => { if (doc.visibilityState === 'hidden') settle(true); };
    pending = state;
    notify(state, 'clear');
    win.addEventListener('blur', seen);
    win.addEventListener('pagehide', seen);
    doc.addEventListener('visibilitychange', hidden);
    timer = win.setTimeout(() => settle(false), WAIT_MS);
    doc.dispatchEvent(new win.CustomEvent(ATTEMPT_EVENT, { detail: { app, url: state.url } }));
    launch(app);
  }

  /** Ouvre une adresse https (Discord ou non) : application d'abord si possible. false si rien n'a été lancé. */
  function open(url, { newTab = false, anchor = null } = {}) {
    const text = String(url ?? '');
    if (!/^https:\/\//i.test(text) || pending) return false;
    const state = { url: text, newTab: Boolean(newTab), anchor };
    if (desktop && appUrl(text) && !prefersWeb()) { attempt(state); return true; }
    if (!openWeb(text, state.newTab)) notify(state, 'blocked');
    return true;
  }

  /** « Essayer l'application Discord » : choix « web » oublié, nouvelle tentative (geste de l'utilisateur). */
  function retry(state) {
    storage.clear();
    if (!pending) attempt({ ...state });
  }

  /** Délégation sur le document : clic gauche simple sur un lien Discord. */
  function onClick(event) {
    if (!desktop) return;
    const hit = discordClick(event);
    if (!hit) return;
    if (pending) { event.preventDefault(); return; }
    if (prefersWeb()) return;
    event.preventDefault();
    attempt({ url: hit.url, newTab: hit.newTab, anchor: hit.link });
  }

  return { open, retry, onClick, get pending() { return pending; }, desktop };
}

/* ------------------------------------------------------------------ message de repli (navigateur) */
let note = null;

/** Message role="status" juste après la zone du lien (dans la fiche ouverte : hors d'elle, il serait inerte). */
function notifier(opener) {
  return (state, kind) => {
    if (kind === 'clear') { note?.remove(); note = null; return; }
    note?.remove();
    const anchor = state.anchor?.isConnected ? state.anchor : null;
    note = document.createElement('div');
    note.className = 'fs-discord-note';
    note.setAttribute('role', 'status');
    const host = anchor ? anchor.closest('[data-fs-commerce], p, li') || anchor : null;
    if (host) host.after(note);
    else (document.querySelector('dialog[open]') || document.body).append(note);
    const button = (text, className, onclick, label) => {
      const element = document.createElement('button');
      element.type = 'button';
      element.className = className;
      element.textContent = text;
      if (label) element.setAttribute('aria-label', label);
      element.addEventListener('click', onclick);
      return element;
    };
    const dismiss = () => { note?.remove(); note = null; if (anchor?.isConnected) anchor.focus({ preventScroll: true }); };
    const parts = [];
    const text = document.createElement('p');
    text.className = 'fs-discord-note-text';
    text.textContent = `${kind === 'blocked' ? t('nav.discord.blocked') : t('nav.discord.web')} `;
    if (kind === 'blocked') {
      const link = document.createElement('a');
      link.className = 'fs-link fs-discord-note-link';
      link.href = state.url;
      link.setAttribute('data-fs-discord-web', '');
      if (state.newTab) { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
      link.append(t('nav.discord.open_web'));
      if (state.newTab) {
        const hint = document.createElement('span');
        hint.className = 'fs-visually-hidden';
        hint.textContent = ` ${t('common.new_tab')}`;
        link.append(hint);
      }
      text.append(link);
    }
    parts.push(text);
    if (opener.desktop && appUrl(state.url)) {
      parts.push(button(t('nav.discord.retry'), 'fs-link fs-discord-note-link fs-discord-note-retry', () => {
        if (anchor?.isConnected) anchor.focus({ preventScroll: true });
        opener.retry(state);
      }));
    }
    parts.push(button('×', 'fs-discord-note-close', dismiss, t('nav.discord.close')));
    /* Région vide insérée d'abord, texte posé ensuite : annoncé par les lecteurs d'écran. */
    const box = note;
    window.setTimeout(() => { if (note === box) box.append(...parts); }, 50);
  };
}

if (typeof document !== 'undefined' && !window.FSDiscord) {
  let show = () => {};
  const opener = createOpener(window, { notify: (state, kind) => show(state, kind) });
  show = notifier(opener);
  window.FSDiscord = Object.freeze({ open: opener.open });
  document.addEventListener('click', opener.onClick);
}
